import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import EosBackdrop from "../EosBackdrop";
import { useLandingPage } from "./useLandingPage";
import MarketTicker from "./MarketTicker";
import StatCounter from "./StatCounter";
import Reveal from "./Reveal";
import "./LandingPage.scss";

const LandingPage: React.FC = () => {
  const { highlights, iconStrip, stats } = useLandingPage();

  return (
    <div className="eos-dark-shell">
      <EosBackdrop />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header variant="public" stickyBelow={<MarketTicker />} />

        <main className="flex-1">
          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-3xl text-center animate-fade-up">
              <div className="hero-badge mb-6 inline-flex items-center px-3 py-1 text-sm font-medium text-accent-300">
                <TrendingUp className="mr-2 h-4 w-4" />
                Smarter trading decisions with real-time intelligence
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                <span className="text-slate-100">Build confidence in every trade with a </span>
                <span className="eos-gradient-text">modern trading command center.</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
                Engineering of Stocks helps traders and investors automate strategy execution, monitor markets, and stay
                aligned with their goals through a clear and intelligent workflow.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/login" className="eos-btn-glow">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <a href="#about" className="hero-cta-ghost inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-slate-200">
                  Learn More
                </a>
              </div>
            </div>

            <Reveal className="mt-16 flex flex-wrap justify-center gap-6">
              {iconStrip.map((Icon, index) => (
                <div
                  key={index}
                  className="icon-strip-tile flex h-16 w-16 items-center justify-center rounded-2xl text-accent-300 animate-float-medium"
                  style={{ animationDelay: `${index * 0.4}s` }}
                >
                  <Icon className="h-7 w-7" strokeWidth={1.75} />
                </div>
              ))}
            </Reveal>

            <Reveal className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
              {stats.map((stat) => (
                <StatCounter key={stat.label} stat={stat} />
              ))}
            </Reveal>
          </section>

          <section id="about" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
            <Reveal className="about-panel p-8 sm:p-10">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-accent-400">
                  About Engineering of Stocks
                </p>
                <h2 className="mt-3 text-3xl font-semibold text-slate-100 sm:text-4xl">
                  A platform designed for disciplined, data-led trading.
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-400">
                  Our mission is to simplify complex trading workflows by combining algorithmic monitoring, transparent
                  execution insights, and a modern user experience for traders at every level.
                </p>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {highlights.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="feature-card group p-6 transition-transform duration-300 hover:-translate-y-1"
                    >
                      <div className="feature-icon flex h-12 w-12 items-center justify-center rounded-full text-accent-300 transition-colors duration-300 group-hover:text-accent-200">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="mt-4 text-xl font-semibold text-slate-100">{item.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-slate-400">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
